import { useState, useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { ThemeProvider, createTheme } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import LoginPage from './pages/LoginPage';
import AdminLayout from './components/AdminLayout/AdminLayout';
import AdminLandingPage from './components/AdminLandingPage/AdminLandingPage';
import RoleAccessManagement from './components/RoleAccessManagement/RoleAccessManagement';
import QRCodeGenerator from './components/QRCodeGenerator/QRCodeGenerator';
import CompanySites from './components/CompanySites/CompanySites';
import EquipmentPage from './components/EquipmentPage/EquipmentPage';
import Certificates from './components/Certificates/Certificates';
import Notification from './components/Notification/Notification';
import Requests from './components/Requests/Requests';
import Worklists from './components/Worklists/Worklists';
import Settings from './components/Settings/Settings';
import { setTokens, clearTokens } from './services/authService';
import { getHomeRouteForRole } from './utils/userRoles';
import './App.css';

// Material palette from reference: dark primary CTA, success green, light UI base
const theme = createTheme({
  palette: {
    primary: { main: '#20232a' },      // strong dark blue (CTA / primary actions)
    secondary: { main: '#70c1b3' },    // teal accent (from chart palette)
    success: { main: '#8cc63f' },      // reference green (available / pass)
    warning: { main: '#f4d03f' },      // soft yellow from chart palette
    error: { main: '#d32f2f' },
    background: { default: '#f1f5f9', paper: '#ffffff' },
  },
  shape: { borderRadius: 8 },
  typography: {
    fontFamily: '"Inter", "Roboto", "Helvetica", "Arial", sans-serif',
    h4: { fontWeight: 700 },
    h5: { fontWeight: 700 },
    h6: { fontWeight: 600 },
  },
});

function getInitialUserData() {
  try {
    const stored = localStorage.getItem('userData');
    return stored ? JSON.parse(stored) : null;
  } catch {
    localStorage.removeItem('userData');
    return null;
  }
}

function App() {
  const [userData, setUserData] = useState(getInitialUserData);

  const handleLoginSuccess = (data) => {
    setTokens(data.jwtToken, data.refreshToken);
    localStorage.setItem('userData', JSON.stringify(data));
    setUserData(data);
  };

  const handleLogout = () => {
    clearTokens();
    localStorage.removeItem('userData');
    setUserData(null);
  };

  useEffect(() => {
    const onSessionExpired = () => setUserData(null);
    window.addEventListener('auth:sessionExpired', onSessionExpired);
    return () => window.removeEventListener('auth:sessionExpired', onSessionExpired);
  }, []);

  const getRouteForRole = (roleId) => getHomeRouteForRole(roleId);

  const renderProtected = (Component, props) => {
    if (!userData) return <Navigate to="/" replace />;
    return <Component {...props} />;
  };

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <BrowserRouter basename={process.env.PUBLIC_URL || '/'}>
        <Routes>
        <Route
          path="/"
          element={
            userData ? (
              <Navigate to={getRouteForRole(userData.userDetails?.roleId)} replace />
            ) : (
              <LoginPage onLoginSuccess={handleLoginSuccess} />
            )
          }
        />
        <Route path="/admin" element={renderProtected(AdminLayout, { userData, onLogout: handleLogout })}>
          <Route index element={<AdminLandingPage />} />
          <Route path="company-sites" element={<CompanySites />} />
          <Route path="equipment" element={<EquipmentPage />} />
          <Route path="qr-codes" element={<QRCodeGenerator />} />
          <Route path="certificates" element={<Certificates />} />
          <Route path="notifications" element={<Notification />} />
          <Route path="requests" element={<Requests />} />
          <Route path="role-access-management" element={<RoleAccessManagement />} />
          <Route path="worklists" element={<Worklists />} />
          <Route path="settings" element={<Settings />} />
        </Route>
        <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </BrowserRouter>
    </ThemeProvider>
  );
}

export default App;
